import { ListItemText } from "@mui/material";
import { useMemo } from "react";
import { useDebounce } from "../hooks/use-debounce";
import {
  UseSearchPatientListProps,
  useSearchPatientList,
} from "../queries/list/useSearchPatientList";
import { NonNullableDate } from "../utils/date-time-utility";
import { DEF_PAGINATION, getXtenantId } from "../utils/utils";
import AsyncSelect, { AsyncSelectOption } from "./AsyncSelect";
import Flex from "./Flex";
import Text from "./Text";

interface PatientSelectProps {
  value: AsyncSelectOption | null;
  onChange: (value: AsyncSelectOption | null) => void;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string | boolean;
  patientSearchParam?: UseSearchPatientListProps;
  name?: string;
  handleBlur?: (e: any) => void;
  placeholder?: string;
  excludeUuids?: string[];
  showEmail?: boolean;
}

/**
 * @param value
 * @param onChange
 * @param label
 * @param required
 * @param error
 * @param patientSearchParam
 * @returns AsyncSelect
 *
 * @description using internal patient api
 *
 *
 * @author [Rajkumar Rathod]
 */
const PatientSelect = ({
  value,
  onChange,
  label,
  disabled = false,
  required,
  error,
  patientSearchParam,
  name,
  handleBlur,
  placeholder,
  excludeUuids,
  showEmail = true,
}: PatientSelectProps) => {
  const [searchKey, setSearchKey] = useDebounce<string>("");
  const {
    data: patientList,
    isLoading,
    isFetching,
  } = useSearchPatientList({
    pagination: patientSearchParam?.pagination ?? DEF_PAGINATION,
    searchString: searchKey,
    xTENANTID: patientSearchParam?.xTENANTID ?? getXtenantId(),
    ...patientSearchParam,
  });

  const options: AsyncSelectOption[] = useMemo(() => {
    const allOptions =
      patientList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.firstName + " " + it?.lastName,
        birthDate: it?.birthDate,
        email: it?.email ?? "",
      })) ?? [];
    if (excludeUuids?.length) {
      return allOptions.filter((opt) => !excludeUuids.includes(opt.value));
    }
    return allOptions;
  }, [patientList?.data?.content, excludeUuids]);

  return (
    <AsyncSelect
      required={required}
      error={error}
      label={label}
      loading={isFetching || isLoading}
      disabled={disabled}
      placeholder={placeholder ?? "Search & Select Patient"}
      enableFilterOption
      renderCustomLabel={(option) => (
        <ListItemText
          primary={
            <Flex gap={1}>
              <Text>{option.label}</Text>
              <Text>{NonNullableDate(option.birthDate as any)}</Text>
            </Flex>
          }
          secondary={
            showEmail ? (
              <Text variant="subtitle2">
                {option?.email ? `(${option.email})` : ""}
              </Text>
            ) : undefined
          }
        />
      )}
      options={options}
      value={value}
      onSearch={setSearchKey}
      onChange={onChange}
      name={name}
      handleBlur={handleBlur}
    />
  );
};

export default PatientSelect;
